import _ from "lodash";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { Popper } from "@/components/atoms/Popper";
import { List } from "@/components/organisms/List";
import { NodeList } from "@/components/organisms/Node/NodeList";
import { useScope } from "@/hooks/useScope";
import { Objects } from "@/objects";
import { icons } from "@/objects/@icons";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";

export function RichTextEditorReferencePopper({
  children,
  onAdd = () => null,
  isOpenInitial = false,
}: {
  children: ReactNode;
  onAdd?: (value) => void;
  isOpenInitial?: boolean;
}) {
  const objectsWithReferenceValueOptions = useMemo(
    () =>
      Object.values(Objects).filter(
        (object) =>
          object?.getReferenceValueOptions != undefined &&
          object.typename !== "ThreadObject",
      ),
    [],
  );
  const [isOpen, setIsOpenState] = useState(isOpenInitial);
  const [typename, setTypename] = useState<string | null>(null);
  const { entityIdentifiers } = useScope();

  const setIsOpen = useCallback((value: boolean) => {
    setIsOpenState(value);
    if (!value) {
      setTypename(null);
    }
  }, []);

  if (entityIdentifiers.length === 0) {
    return null;
  }

  return (
    <Popper
      anchor={children}
      isOpenState={[isOpen, setIsOpen]}
      placement={"top-end"}
      floatingStyle={{
        minWidth: "200px",
        height: "max-content",
      }}
    >
      {typename == null && (
        <List
          data={objectsWithReferenceValueOptions}
          onItemClick={(object) => {
            setTypename(object.typename);
          }}
          className="p-1"
        >
          <List.Items>
            {(object: any) => (
              <List.Item
                value={object}
                data-testid={`reference-popper-object-${(object?.humanName || "").toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="flex flex-row items-center justify-between gap-2">
                  <Typography>
                    {_.capitalize((object?.humanName || "").replace("-", " "))}
                  </Typography>
                  <Button variant="ghost" size="icon-xs">
                    <icons.chevronRight />
                  </Button>
                </div>
              </List.Item>
            )}
          </List.Items>
        </List>
      )}
      {typename != null && (
        <div className="flex flex-col flex-1 min-h-0">
          <NodeList
            {...Objects[typename].getReferenceValueOptions(entityIdentifiers)}
            onSelectOption={(option: any) => {
              onAdd(option);
              setTypename(null);
              setIsOpen(false);
            }}
            size="md"
          />
        </div>
      )}
    </Popper>
  );
}
